import { io } from 'socket.io-client';
import { useRealtimeStore } from '../store/realtimeStore';

/**
 * Socket.IO client for the back-office dashboard.
 *
 * Usage:
 *   import { connectSocket, disconnectSocket } from '../lib/socket';
 *
 *   // After auth, connect and join venue room:
 *   connectSocket(authToken, venueId, queryClient);
 *
 *   // On logout:
 *   disconnectSocket();
 *
 * Events received trigger TanStack Query cache invalidation
 * so dashboard data refreshes automatically.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

let socket = null;
let queryClientRef = null;

/**
 * Connect to the server's Socket.IO and join the venue room.
 * @param {string} token - JWT auth token
 * @param {string} venueId - Venue UUID to join
 * @param {import('@tanstack/react-query').QueryClient} [queryClient] - Optional QueryClient for auto-invalidation
 */
export function connectSocket(token, venueId, queryClient) {
  if (socket?.connected) {
    disconnectSocket();
  }

  queryClientRef = queryClient || null;
  const store = useRealtimeStore.getState();
  store.setStatus('connecting');

  socket = io(API_BASE, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
  });

  socket.on('connect', () => {
    store.setStatus('connected');
    store.setRoom(`venue:${venueId}`);
    // eslint-disable-next-line no-console
    console.log('[ws] Connected to server');
  });

  socket.on('disconnect', (reason) => {
    store.setStatus('disconnected');
    // eslint-disable-next-line no-console
    console.log('[ws] Disconnected:', reason);
  });

  socket.on('connect_error', (err) => {
    store.setStatus('disconnected');
    // eslint-disable-next-line no-console
    console.warn('[ws] Connection error:', err.message);
  });

  // --- Real-time event handlers ---

  // Sale closed → refresh sales dashboard data
  socket.on('sale:closed', (payload) => {
    store.pushEvent('sale:closed', payload);
    queryClientRef?.invalidateQueries({ queryKey: ['dashboard', 'sales'] });
    queryClientRef?.invalidateQueries({ queryKey: ['analytics', 'revenue'] });
  });

  // New alert → refresh alerts
  socket.on('alert:new', (payload) => {
    store.pushEvent('alert:new', payload);
    queryClientRef?.invalidateQueries({ queryKey: ['dashboard', 'alerts'] });
  });

  // Inventory low → refresh inventory
  socket.on('inventory:low', (payload) => {
    store.pushEvent('inventory:low', payload);
    queryClientRef?.invalidateQueries({ queryKey: ['dashboard', 'inventory'] });
    queryClientRef?.invalidateQueries({ queryKey: ['analytics', 'inventory-alerts'] });
  });

  // Order status change → refresh ops
  socket.on('order:status', (payload) => {
    store.pushEvent('order:status', payload);
    queryClientRef?.invalidateQueries({ queryKey: ['dashboard', 'ops'] });
  });

  // Staff events
  socket.on('shift:clock_in', (payload) => {
    store.pushEvent('shift:clock_in', payload);
    queryClientRef?.invalidateQueries({ queryKey: ['dashboard', 'staff'] });
  });

  socket.on('shift:clock_out', (payload) => {
    store.pushEvent('shift:clock_out', payload);
    queryClientRef?.invalidateQueries({ queryKey: ['dashboard', 'staff'] });
  });
}

/**
 * Disconnect from Socket.IO and reset state.
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  queryClientRef = null;
  useRealtimeStore.getState().reset();
}

/**
 * Get the raw socket instance (for advanced usage).
 */
export function getSocket() {
  return socket;
}
